import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Badge, Form, InputGroup, Spinner } from 'react-bootstrap';
import UserAvatar from './UserAvatar';
import PaginationBar from './PaginationBar';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

const ROLE_META = {
  admin:   { bg: 'danger',    icon: 'bi-shield-fill', label: 'Admin'  },
  editor:  { bg: 'primary',   icon: 'bi-pencil-fill', label: 'Editor' },
  viewer:  { bg: 'secondary', icon: 'bi-eye-fill',    label: 'Viewer' },
};

const LEVEL_META = {
  'all-island': { bg: 'info',    label: 'All Island'  },
  province:     { bg: 'primary', label: 'Province'    },
  district:     { bg: 'success', label: 'District'    },
  dsd:          { bg: 'warning', label: 'DS Division' },
};

function UserTable({ users, status, loading, onApprove, onSuspend, onActivate, locationData, currentUserEmail }) {
  const navigate = useNavigate();
  const [search, setSearch]     = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage]         = useState(1);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.access_type || '').toLowerCase().includes(q) ||
      (u.access_level || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleSearch   = v => { setSearch(v); setPage(1); };
  const handlePageSize = v => { setPageSize(Number(v)); setPage(1); };

  // ── Location name helpers ──────────────────────────────────────────────────
  const getAllDistricts = () => Object.values(locationData.districts || {}).flat();
  const getAllDivisions = () => Object.values(locationData.divisions || {}).flat();

  const getLocationLabel = (user) => {
    if (user.access_level === 'all-island') return 'All Island';
    const lookup = (codes, list) => {
      if (!codes && codes !== 0) return '—';
      const arr = String(codes).split(',').map(c => c.trim()).filter(Boolean);
      if (!arr.length) return '—';
      return arr.map(code => list.find(x => x.code === code)?.name || code).join(', ');
    };
    if (user.access_level === 'province') return lookup(user.allowed_provinces, locationData.provinces || []);
    if (user.access_level === 'district') return lookup(user.allowed_districts, getAllDistricts());
    if (user.access_level === 'dsd')      return lookup(user.allowed_dsds,      getAllDivisions());
    return '—';
  };

  const formatDate = d => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return d; }
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3 text-muted">Loading users…</p>
      </div>
    );
  }

  return (
    <>
      {/* Toolbar */}
      <div className="um-toolbar d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
        <InputGroup className="um-search">
          <InputGroup.Text className="bg-white border-end-0">
            <i className="bi bi-search text-muted"></i>
          </InputGroup.Text>
          <Form.Control
            className="border-start-0"
            placeholder="Search name, email, role…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
          />
          {search && (
            <Button variant="outline-secondary" onClick={() => handleSearch('')}>
              <i className="bi bi-x"></i>
            </Button>
          )}
        </InputGroup>
        <div className="d-flex align-items-center gap-2">
          <small className="text-muted text-nowrap">Rows per page</small>
          <Form.Select size="sm" style={{ width: 'auto' }} value={pageSize} onChange={e => handlePageSize(e.target.value)}>
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </Form.Select>
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div className="um-empty text-center py-5">
          <i className="bi bi-people text-muted" style={{ fontSize: '2.5rem' }}></i>
          <p className="mt-3 text-muted mb-0">
            {search ? 'No users match your search.' : `No ${status} users.`}
          </p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table className="um-table align-middle mb-0">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Access Level</th>
                  <th>Allowed Locations</th>
                  <th>Joined</th>
                  <th>Last Login</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(user => {
                  const roleMeta  = ROLE_META[user.access_type]  || ROLE_META.viewer;
                  const levelMeta = LEVEL_META[user.access_level] || null;
                  const isSelf    = user.email === currentUserEmail;
                  return (
                    <tr key={user.email}>
                      {/* User */}
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <UserAvatar user={user} />
                          <div className="min-width-0">
                            <div className="fw-semibold text-truncate" style={{ maxWidth: 180 }}>
                              {user.name || <span className="text-muted fst-italic">No name</span>}
                            </div>
                            <div className="small text-muted text-truncate" style={{ maxWidth: 180 }}>
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* Role */}
                      <td>
                        <Badge bg={roleMeta.bg} className="um-badge">
                          <i className={`bi ${roleMeta.icon} me-1`}></i>{roleMeta.label}
                        </Badge>
                      </td>
                      {/* Access level */}
                      <td>
                        {levelMeta
                          ? <Badge bg={levelMeta.bg} className="um-badge">{levelMeta.label}</Badge>
                          : <span className="text-muted small">—</span>}
                      </td>
                      {/* Locations */}
                      <td className="small text-muted" style={{ maxWidth: 200 }}>
                        <span className="d-inline-block text-truncate" style={{ maxWidth: 190 }}>
                          {getLocationLabel(user)}
                        </span>
                      </td>
                      {/* Dates */}
                      <td className="small text-muted text-nowrap">{formatDate(user.created_date)}</td>
                      <td className="small text-muted text-nowrap">{formatDate(user.last_login)}</td>
                      {/* Actions */}
                      <td>
                        <div className="d-flex justify-content-center gap-2">
                          {isSelf ? (
                            <span className="small text-muted fst-italic">You</span>
                          ) : (
                            <>
                              {status === 'pending' && (
                                <Button size="sm" variant="success" className="um-action-btn" onClick={() => onApprove(user)}>
                                  <i className="bi bi-check-lg me-1"></i>Approve
                                </Button>
                              )}
                              {status === 'active' && (
                                <>
                                  <Button size="sm" variant="outline-primary" className="um-action-btn" onClick={() => navigate('/admin/users/edit', { state: { user, locationData } })}>
                                    <i className="bi bi-pencil me-1"></i>Edit
                                  </Button>
                                  <Button size="sm" variant="outline-warning" className="um-action-btn" onClick={() => onSuspend(user.email)}>
                                    <i className="bi bi-slash-circle me-1"></i>Suspend
                                  </Button>
                                </>
                              )}
                              {status === 'suspended' && (
                                <Button size="sm" variant="outline-success" className="um-action-btn" onClick={() => onActivate(user.email)}>
                                  <i className="bi bi-arrow-counterclockwise me-1"></i>Activate
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <PaginationBar
            currentPage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setPage}
          />
        </>
      )}
    </>
  );
}

export default UserTable;
